import { useParams } from 'react-router-dom';
import { useFilterNavigate } from '../../../helpers/hooks/useFilterNavigate';
import { WidgetProvider } from '../../widgetContext';
import WidgetErrorBoundary from '../../widgetErrorBoundary';
import SelectWidget from '../selectWidget';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../state/store';
import { getTodayInTimezone } from '../../../helpers/utils';
import useFetchDashboardWidgetData from '../../../helpers/hooks/useFetchDashboardWidgetData';
import { appEventsMessaging } from '../../../helpers/hooks/pubSub';
import type { WIDGET_TYPE } from '../../../types/widgets';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { toZonedTime } = require('date-fns-tz');

interface Props {
  type: string;
  widgetData: any; // Initial widget schema for skeleton
  url: string;
  fetchUrl: string;
  childWidgets: any[];
  isTimelessWidget: boolean;
  dashboard: string;
}

/**
 * Widget loader for D-level widgets that have independent queries
 * These widgets fetch their data separately from the main dashboard query
 * for parallel loading support
 */
const IndependentDashboardWidgetLoader = ({
  type: widgetType,
  widgetData: initialWidgetData,
  url: widgetUrl,
  fetchUrl,
  childWidgets,
  isTimelessWidget,
  dashboard
}: Props) => {
  const routeMatch = useParams();
  const navigateTo = useFilterNavigate();
  const [showTimelessUI, setShowTimelessUI] = useState(false);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(new Date());
  const { company, filters } = useSelector((state: RootState) => state);
  const time_zone_offset = (company?.company_details as any)?.time_zone_offset;
  const enableValueAnimation = (company?.company_details as any)?.enable_clickhouse ?? false;
  const dateFilters = useSelector((state: RootState) => state.filters.dates);

  // Fetch widget data independently
  // For timeless widgets (e.g., Future Orders), don't refetch when date changes
  const { data, isLoading, error, reload } = useFetchDashboardWidgetData({
    dashboard,
    dashboardWidget: widgetUrl,
    fetchUrl,
    filters: filters as unknown as {
      locations: number[];
      dates: [string, string];
      timeRange: [[number, number], [number, number]];
    },
    shouldFetch: true,
    isTimelessWidget // Pass through - prevents refetch on date change
  });

  // Get widget data from response
  // Use empty object while loading to prevent widget from trying to render incomplete data
  // The widgetLoading prop will show loading state via WidgetProvider
  const widgetData =
    data?.widget || (isLoading ? { type: widgetType, title: '' } : initialWidgetData);

  // Subscribe to dashboard refresh events (Pusher)
  useEffect(() => {
    if (!dashboard) return;

    const subscription = appEventsMessaging(`dashboard_updated_${dashboard}`, {
      onSubscribe() {
        reload();
      }
    });
    subscription.subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [dashboard, reload]);

  useEffect(() => {
    if (widgetData && !isLoading) {
      // Get today's date in company timezone
      const today = getTodayInTimezone(time_zone_offset);
      const selectedDate = dateFilters?.[0];
      setShowTimelessUI(today !== selectedDate);

      // Set last updated time using the same timezone conversion
      const currentDate = new Date();
      const timezoneAdjustedCurrentDate = time_zone_offset
        ? toZonedTime(currentDate, time_zone_offset)
        : currentDate;
      setLastUpdatedTime(timezoneAdjustedCurrentDate);
    }
  }, [JSON.stringify(widgetData), isLoading, dateFilters?.[0], time_zone_offset]);

  const handleWidgetOnClick = () => {
    if (childWidgets.length > 1) {
      navigateTo(`/${routeMatch.dashboard}/${widgetUrl}`);
    }
  };

  return (
    <WidgetErrorBoundary>
      <WidgetProvider
        widgetType={widgetType}
        widgetData={widgetData}
        widgetUrl={widgetUrl}
        widgetOnClick={handleWidgetOnClick}
        childWidgets={childWidgets}
        widgetLoading={isLoading}
        widgetLoadError={Boolean(error)}
        isTimelessWidget={isTimelessWidget && showTimelessUI}
        lastUpdatedTime={lastUpdatedTime}
        refreshData={reload}
        enableValueAnimation={enableValueAnimation}
      >
        <SelectWidget widgetType={widgetType as WIDGET_TYPE} />
      </WidgetProvider>
    </WidgetErrorBoundary>
  );
};

export default IndependentDashboardWidgetLoader;
